import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { PoolPlayerPublic, MessagePublic, AgeGroupPublic } from "@dugads/shared";
import { playerDisplayName } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ResponsiveTable } from "@/components/responsive-table";
import { AlertTriangle, ChevronDown, ChevronRight, Users, Clock, BarChart3 } from "lucide-react";
import { ListBackButton } from "@/components/list-back-button";
import { SendMessageButton } from "@/components/send-message-button";
import { MessageResponseDashboard } from "@/components/message-response-dashboard";
import { useSchoolScope } from "@/lib/use-school-scope";

export default function AgeGroupDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { selectedSchoolId } = useSchoolScope();
  const [ageGroup, setAgeGroup] = useState<AgeGroupPublic | null>(null);
  const [players, setPlayers] = useState<PoolPlayerPublic[]>([]);
  const [messages, setMessages] = useState<MessagePublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messagesExpanded, setMessagesExpanded] = useState(false);
  const [dashboardMessageId, setDashboardMessageId] = useState<string | null>(null);

  const refreshPool = useCallback(() => {
    if (!id) return;
    api
      .getAgeGroupPlayers(id, selectedSchoolId ?? "")
      .then((res) => setPlayers(res.players))
      .catch((e) =>
        setError(
          e instanceof api.ApiError ? e.message : "Failed to load player pool",
        ),
      );
  }, [id, selectedSchoolId]);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError(null);

    api.getAllAgeGroups()
      .then((res) => {
        const ag = res.age_groups.find((a) => a.id === id) ?? null;
        setAgeGroup(ag);
      })
      .catch(() => {});

    api
      .getAgeGroupPlayers(id, selectedSchoolId ?? "")
      .then((res) => setPlayers(res.players))
      .catch((e) =>
        setError(
          e instanceof api.ApiError ? e.message : "Failed to load player pool",
        ),
      )
      .finally(() => setLoading(false));

    // Load message history
    setMessagesLoading(true);
    api
      .getAgeGroupMessages(id, selectedSchoolId ?? "")
      .then((res) => setMessages(res.messages))
      .catch(() => {})
      .finally(() => setMessagesLoading(false));
  }, [id, selectedSchoolId]);

  // 079 -- stay in sync when a player is created/updated from a sibling pane
  useEffect(() => {
    const handler = () => refreshPool();
    window.addEventListener("player:created", handler);
    window.addEventListener("player:updated", handler);
    return () => {
      window.removeEventListener("player:created", handler);
      window.removeEventListener("player:updated", handler);
    };
  }, [refreshPool]);

  return (
    <div className="flex flex-col gap-6" data-page="age-group-detail" data-state={loading ? "loading" : error ? "error" : "ready"}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-region="header">
        <div className="flex items-center gap-3">
          <ListBackButton to="/teams" label="Teams" />
          <div>
            <h1 className="text-2xl font-semibold">Player Pool</h1>
            <p className="text-muted-foreground">
              Eligible active players for this age group
            </p>
          </div>
        </div>
        {id && (
          <SendMessageButton
            contextType="age_group"
            contextId={id}
            contextLabel="Age Group"
            schoolId={selectedSchoolId ?? undefined}
            variant="outline"
            showLabel
          />
        )}
      </div>

      {ageGroup && <MatchDefaultsSection ageGroup={ageGroup} setAgeGroup={setAgeGroup} />}

      {loading && <p className="text-muted-foreground">Loading...</p>}
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {!loading && !error && players.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-12">
          <p className="text-muted-foreground">No eligible players found</p>
          <p className="text-sm text-muted-foreground">
            Players must be active and have a date of birth within the age
            group&apos;s birth year range
          </p>
        </div>
      )}

      {!loading && !error && players.length > 0 && (
        <>
          <p className="text-sm text-muted-foreground">
            {players.length} eligible player{players.length !== 1 ? "s" : ""}
          </p>
          {/* 203 -- one columns array drives the sm+ table and the <sm cards. */}
          <div data-region="player-pool">
            <ResponsiveTable<PoolPlayerPublic>
              columns={[
                { key: "name", header: "Name", cardRole: "title", className: "font-medium", render: (p) => playerDisplayName(p.first_name, p.surname) },
                { key: "dob", header: "Date of Birth", className: "text-muted-foreground", render: (p) => p.date_of_birth },
                { key: "grade", header: "Grade", className: "text-muted-foreground", render: (p) => p.grade ?? "--" },
                { key: "code", header: "Player Code", className: "text-muted-foreground font-mono text-xs", render: (p) => p.player_code ?? "--" },
                {
                  key: "eligibility",
                  header: "Eligibility",
                  render: (p) => (
                    <>
                      {p.source === "play_up_override" && (
                        <Badge variant="outline" className="border-amber-500 text-amber-700" title="Added via play-up override (also_eligible_for)">
                          Play-up override
                        </Badge>
                      )}
                      {p.source === "open_rollup" && (
                        <Badge variant="outline" className="border-sky-500 text-sky-700" title="Rolled up from a strict age band into this Open group">
                          Open rollup
                        </Badge>
                      )}
                      {p.source === "natural" && (
                        <Badge variant="secondary" title="Natural age eligibility">
                          Natural
                        </Badge>
                      )}
                    </>
                  ),
                },
                {
                  key: "indemnity",
                  header: "Indemnity",
                  render: (p) =>
                    p.indemnity_complete ? (
                      <Badge variant="default">Complete</Badge>
                    ) : (
                      <Badge variant="destructive" className="flex w-fit items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Incomplete
                      </Badge>
                    ),
                },
              ]}
              rows={players}
              rowKey={(p) => p.id}
              rowProps={(p) => ({ "data-item": `player-${p.id}` })}
            />
          </div>
        </>
      )}

      {/* Message History Section */}
      <div className="rounded-lg border border-border" data-region="message-history">
        <Button
          variant="ghost"
          className="flex w-full items-center justify-between px-4 py-3"
          onClick={() => setMessagesExpanded(!messagesExpanded)}
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            {messagesExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Message History
            {messages.length > 0 && (
              <Badge variant="secondary">{messages.length}</Badge>
            )}
          </span>
        </Button>

        {messagesExpanded && (
          <div className="border-t border-border px-4 py-3">
            {messagesLoading && (
              <p className="text-sm text-muted-foreground">Loading messages...</p>
            )}

            {!messagesLoading && messages.length === 0 && (
              <p className="text-sm text-muted-foreground">
                No messages sent to this age group yet
              </p>
            )}

            {!messagesLoading && messages.length > 0 && (
              <div className="flex flex-col gap-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className="rounded-md border border-border p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">
                        {msg.sender_name}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {new Date(msg.sent_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-1 text-sm">{msg.content}</p>
                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {msg.recipient_count} recipient{msg.recipient_count !== 1 ? "s" : ""}
                      </span>
                      {msg.response_options && msg.response_summary && (
                        <button
                          type="button"
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                          onClick={() => setDashboardMessageId(dashboardMessageId === msg.id ? null : msg.id)}
                        >
                          <BarChart3 className="h-3 w-3" />
                          {Object.values(msg.response_summary).reduce((a, b) => a + b, 0)} response{Object.values(msg.response_summary).reduce((a, b) => a + b, 0) !== 1 ? "s" : ""}
                        </button>
                      )}
                    </div>
                    {dashboardMessageId === msg.id && msg.response_options && (
                      <div className="mt-3">
                        <MessageResponseDashboard
                          messageId={msg.id}
                          responseOptions={msg.response_options}
                          onClose={() => setDashboardMessageId(null)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MatchDefaultsSection({ ageGroup, setAgeGroup }: { ageGroup: AgeGroupPublic; setAgeGroup: (ag: AgeGroupPublic) => void }) {
  const canEdit = ageGroup._actions.includes("edit");
  const [firstHalf, setFirstHalf] = useState(ageGroup.default_first_half_mins);
  const [breakMins, setBreakMins] = useState(ageGroup.default_break_mins);
  const [secondHalf, setSecondHalf] = useState(ageGroup.default_second_half_mins);
  const [saving, setSaving] = useState(false);

  const changed = firstHalf !== ageGroup.default_first_half_mins || breakMins !== ageGroup.default_break_mins || secondHalf !== ageGroup.default_second_half_mins;

  async function save() {
    setSaving(true);
    try {
      const res = await api.updateAgeGroup(ageGroup.id, { default_first_half_mins: firstHalf, default_break_mins: breakMins, default_second_half_mins: secondHalf });
      setAgeGroup(res.age_group);
    } catch { /* silent */ }
    setSaving(false);
  }

  return (
    <div className="flex items-center gap-4 rounded-lg border border-border px-4 py-3" data-region="match-defaults">
      <span className="text-sm font-medium text-muted-foreground shrink-0">Match defaults</span>
      <div className="flex items-center gap-2 text-sm">
        <label className="text-xs text-muted-foreground">Half</label>
        <Input type="number" min={1} max={60} value={firstHalf} disabled={!canEdit}
          onChange={(e) => { const v = Number(e.target.value); setFirstHalf(v); setSecondHalf(v); }}
          className="h-7 w-16 text-center text-xs" />
        <span className="text-xs text-muted-foreground">min × 2 +</span>
        <label className="text-xs text-muted-foreground">Break</label>
        <Input type="number" min={0} max={30} value={breakMins} disabled={!canEdit}
          onChange={(e) => setBreakMins(Number(e.target.value))}
          className="h-7 w-16 text-center text-xs" />
        <span className="text-xs text-muted-foreground">min</span>
        <span className="text-xs text-muted-foreground">= {firstHalf + breakMins + secondHalf} min total</span>
      </div>
      {canEdit && changed && (
        <Button variant="default" size="sm" onClick={save} disabled={saving} className="h-7 text-xs">
          {saving ? "Saving..." : "Save"}
        </Button>
      )}
    </div>
  );
}
